import { merchantName } from '../features/agency/merchantName';
import { useSearchParams } from "react-router-dom";
import { ClickRecords } from "../features/advertising/ClickRecords";
import { useAdvertisingReportFilters } from "../features/advertising/reportFilters";
import {
  Panel,
  Notice,
  input,
  button,
  useCan,
  useResource,
} from "../features/agency/ui";
import * as api from "../features/advertising/service";
import {
  PLACEMENTS,
  groupCounts,
  type Placement,
} from "../features/advertising/model";
const percent = (n: number, impressions: number) =>
  impressions > 0 ? `${(n * 100).toFixed(2)}%` : "—";
function Trend({ days }: { days: ReturnType<typeof groupCounts> }) {
  if (!days.length)
    return (
      <p className="text-sm text-gray-400 py-8 text-center">
        此范围暂无广告事件
      </p>
    );
  const max = Math.max(
    1,
    ...days.map((d) => Math.max(d.impressions, d.clicks)),
  );
  const x = (i: number) => 40 + (i * 620) / Math.max(1, days.length - 1);
  const y = (v: number) => 175 - (v / max) * 145;
  return (
    <div>
      <div className="flex gap-5 text-xs mb-3">
        <span className="text-blue-600">● 展示次数 PV</span>
        <span className="text-emerald-600">● 点击次数</span>
      </div>
      <svg
        viewBox="0 0 700 215"
        role="img"
        aria-label="广告每日展示与点击趋势"
        className="w-full max-h-72"
      >
        <path d="M40 20V175H660" stroke="#e5e7eb" fill="none" />
        <text x="0" y="28" fontSize="11" fill="#6b7280">
          {max}
        </text>
        {(["impressions", "clicks"] as const).map((key, index) => (
          <g key={key}>
            <polyline
              points={days.map((d, i) => `${x(i)},${y(d[key])}`).join(" ")}
              fill="none"
              stroke={index ? "#059669" : "#2563eb"}
              strokeWidth="2.5"
            />
            {days.map((d, i) => (
              <circle
                key={d.key}
                cx={x(i)}
                cy={y(d[key])}
                r="3"
                fill={index ? "#059669" : "#2563eb"}
              >
                <title>
                  {d.key} {key === "impressions" ? "展示" : "点击"} {d[key]}
                </title>
              </circle>
            ))}
          </g>
        ))}
        <text x="40" y="205" fontSize="11" fill="#6b7280">
          {days[0].key}
        </text>
        <text x="660" y="205" textAnchor="end" fontSize="11" fill="#6b7280">
          {days.at(-1)?.key}
        </text>
      </svg>
    </div>
  );
}
export function AdvertisingReportsPage() {
  const canView = useCan("advertising.reports.view");
  const filters = useAdvertisingReportFilters();
  const [q, setQ] = useSearchParams();
  const tab = q.get("tab") === "clicks" ? "clicks" : "summary";
  const setTab = (value: string) => {
    const next = new URLSearchParams(q);
    next.set("tab", value);
    next.delete("page");
    next.delete("click_id");
    setQ(next, { replace: true });
  };
  if (!canView) return <Notice error="当前账号无广告报表查看权限" />;
  return <div className="space-y-5">
    <div className="flex flex-wrap gap-2" role="tablist" aria-label="广告报表类型">
      <button role="tab" aria-selected={tab === "summary"} className={`${button} ${tab === "summary" ? "bg-blue-50 text-blue-700" : ""}`} onClick={() => setTab("summary")}>展示与点击统计</button>
      <button role="tab" aria-selected={tab === "clicks"} className={`${button} ${tab === "clicks" ? "bg-blue-50 text-blue-700" : ""}`} onClick={() => setTab("clicks")}>点击与渠道记录</button>
    </div>
    <div role="tabpanel">{tab === "clicks" ? <ClickRecords filters={filters} /> : <AdvertisingSummary filters={filters} />}</div>
  </div>;
}
function AdvertisingSummary({ filters: f }: { filters: ReturnType<typeof useAdvertisingReportFilters> }) {
  const params = f.draft;
  const set = f.set;
  const { start_date, end_date, ad_id, agent_id, placement } = f.submitted;
  const r = useResource(() => api.report({ start_date, end_date, ad_id, agent_id, placement }), [f.key]);
  const d = r.data;
  const days = groupCounts(d?.items ?? [], "date");
  const slots = groupCounts(d?.items ?? [], "placement");
  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-500">
        中国区广告展示与点击数据 · 日期按中国时间统计，最多查询 366 天。
      </p>
      <Panel>
        <form className="flex flex-wrap gap-3" onSubmit={(event) => { event.preventDefault(); f.submit(); }}>
          <label className="text-sm text-gray-500">
            开始日期
            <input
              aria-label="开始日期"
              className={`${input} ml-2`}
              type="date"
              value={params.start_date}
              onChange={(e) => set("start_date", e.target.value)}
            />
          </label>
          <label className="text-sm text-gray-500">
            结束日期
            <input
              aria-label="结束日期"
              className={`${input} ml-2`}
              type="date"
              value={params.end_date}
              min={params.start_date}
              onChange={(e) => set("end_date", e.target.value)}
            />
          </label>
          <input
            aria-label="广告 ID"
            className={input}
            type="number"
            min={1}
            placeholder="广告 ID"
            value={params.ad_id}
            onChange={(e) => set("ad_id", e.target.value)}
          />
          <input
            aria-label="商户 ID"
            className={input}
            type="number"
            min={1}
            placeholder="商户 ID（空为全部）"
            value={params.agent_id}
            onChange={(e) => set("agent_id", e.target.value)}
          />
          <select
            aria-label="广告位"
            className={input}
            value={params.placement}
            onChange={(e) => set("placement", e.target.value)}
          >
            <option value="">全部广告位</option>
            {Object.entries(PLACEMENTS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <button className={button} type="submit">查询</button>
          <button className={button} type="button" onClick={r.reload}>
            刷新
          </button>
        </form>
      </Panel>
      <Notice error={r.error} />
      {r.error && <button className={button} onClick={r.reload}>重试</button>}
      {r.loading && <p className="text-sm text-gray-400">统计加载中…</p>}
      {d && (
        <>
          <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
            {[
              ["展示次数 PV", d.totals.impressions],
              ["展示人数 UV", d.totals.impression_users],
              ["点击次数", d.totals.clicks],
              ["点击人数 UV", d.totals.click_users],
              ["点击率 CTR", percent(d.totals.ctr, d.totals.impressions)],
            ].map(([name, value]) => (
              <div
                key={name}
                className="rounded-lg bg-white border border-gray-200 p-5"
              >
                <p className="text-xs text-gray-500">{name}</p>
                <p className="text-2xl font-semibold mt-3 tabular-nums">
                  {value}
                </p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500">
            UV 为所选范围内服务端去重人数；CTR = 点击次数 ÷
            展示次数。无展示时点击率显示为 —。
          </p>
          <div className="grid gap-5 xl:grid-cols-[1.5fr_1fr]">
            <Panel title="每日趋势">
              <Trend days={days} />
            </Panel>
            <Panel title="广告位对比">
              <p className="text-xs text-gray-500">沿用当前全部筛选条件</p>
              {slots.length ? (
                slots.map((s) => (
                  <div
                    key={s.key}
                    className="space-y-2 border-b border-gray-100 pb-4"
                  >
                    <div className="flex justify-between text-sm">
                      <span>{PLACEMENTS[s.key as Placement]}</span>
                      <span className="text-gray-500">
                        CTR {percent(s.ctr, s.impressions)}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: `${(s.impressions / Math.max(1, ...slots.map((x) => x.impressions))) * 100}%`,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      展示 {s.impressions} 次 · 点击 {s.clicks} 次
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-400">暂无数据</p>
              )}
            </Panel>
          </div>
          <Panel title="明细记录">
            <div className="overflow-auto">
              <table className="w-full min-w-[980px] text-sm text-left">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    {[
                      "日期",
                      "广告 / 修订",
                      "归属",
                      "广告位",
                      "展示 PV",
                      "展示 UV",
                      "点击",
                      "点击 UV",
                      "CTR",
                    ].map((t) => (
                      <th key={t} className="p-3 font-medium">
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((row) => (
                    <tr
                      key={`${row.date}-${row.ad_id}-${row.revision_id}-${row.placement}`}
                      className="border-b border-gray-100"
                    >
                      <td className="p-3">{row.date}</td>
                      <td className="p-3">
                        {row.title}
                        <span className="block text-xs text-gray-400">
                          广告 #{row.ad_id} · 修订 #{row.revision_id}
                        </span>
                      </td>
                      <td className="p-3">
                        {merchantName(row, "平台")}
                      </td>
                      <td className="p-3">{PLACEMENTS[row.placement]}</td>
                      <td className="p-3 tabular-nums">{row.impressions}</td>
                      <td className="p-3 tabular-nums">
                        {row.impression_users}
                      </td>
                      <td className="p-3 tabular-nums">{row.clicks}</td>
                      <td className="p-3 tabular-nums">{row.click_users}</td>
                      <td className="p-3 tabular-nums">
                        {percent(row.ctr, row.impressions)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!d.items.length && (
                <p className="text-center text-sm text-gray-400 py-10">
                  此范围暂无广告事件
                </p>
              )}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
